import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { ChevronLeft, MapPin, Star, Scissors, Clock, Sparkles } from 'lucide-react';
import { Button, Badge, Typography, Spinner } from 'glintly-ui';
import { useShop, bookingService, reviewService } from '../../../api';
import { addToCart } from '../../../redux/slice/cartSlice';
import { openAuthModal } from '../../../utils/openAuthModal';
import Container from '../../../components/container';
import { formatShopAddress } from '../../../utils/shopAddress';
import styles from './salonView.module.css';

function formatDuration(d) {
  if (d == null || d === '') return null;
  if (typeof d === 'number') return `${d} min`;
  return String(d);
}

function localYmd(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export default function SalonView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector((s) => s.auth?.user);
  const token = useSelector((s) => s.auth?.token);

  const { data: shop, isLoading: loading, isError, error } = useShop(id);

  const [selectedService, setSelectedService] = useState(null);
  const [selectedBarber, setSelectedBarber] = useState(null);
  const [selectedDate, setSelectedDate] = useState('');
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    setSelectedService(null);
    setSelectedBarber(null);
    setSelectedDate('');
    setSelectedSlot(null);
    setSlots([]);
  }, [id]);

  useEffect(() => {
    if (!shop) return;
    setSelectedService((prev) => {
      if (prev) return prev;
      return shop.services?.[0] || shop.products?.[0] || null;
    });
  }, [shop]);

  useEffect(() => {
    if (shop?._id) {
      reviewService
        .getShopReviews(shop._id)
        .then((data) => setReviews(Array.isArray(data) ? data : []))
        .catch(() => setReviews([]));
    }
  }, [shop?._id]);

  useEffect(() => {
    if (!shop || !selectedDate) {
      setSlots([]);
      return;
    }
    const svc = selectedService || shop.services?.[0] || shop.products?.[0];
    if (!svc) return;
    bookingService
      .getAvailableSlots(shop._id, selectedDate, {
        serviceId: shop.services?.some((s) => s?._id === svc._id) ? svc._id : undefined,
        productId: shop.products?.some((p) => p?._id === svc._id) ? svc._id : undefined,
        barberId: selectedBarber?._id,
      })
      .then((d) => setSlots(d.slots || []))
      .catch(() => setSlots([]));
  }, [shop, selectedDate, selectedService, selectedBarber]);

  const barbers = shop?.barbers || [];
  const services = shop?.services || [];
  const products = shop?.products || [];

  const hasBookable = services.length > 0 || products.length > 0;

  const nextDays = useMemo(() => {
    const days = [];
    for (let i = 0; i < 14; i++) {
      const d = new Date();
      d.setDate(d.getDate() + i);
      days.push(localYmd(d));
    }
    return days;
  }, []);

  const selectedLabel = useMemo(() => {
    if (!selectedService) return null;
    return selectedService.name;
  }, [selectedService]);

  const handleBook = async () => {
    const svc = selectedService || services[0] || products[0];
    if (!svc) {
      toast.info('Select a service first');
      return;
    }
    if (!selectedSlot) {
      toast.info('Choose a time slot');
      return;
    }
    if (!user || !token) {
      openAuthModal('signin');
      toast.info('Please sign in to book');
      return;
    }

    setBookingLoading(true);
    try {
      const payload = {
        shopId: shop._id,
        barberId: selectedBarber?._id,
        slotStart: selectedSlot.start,
      };
      if (services.some((s) => s?._id === svc._id)) {
        payload.serviceId = svc._id;
      } else {
        payload.productId = svc._id;
      }
      await bookingService.createBooking(payload);
      toast.success('Booking confirmed');
      navigate('/bookings');
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Booking failed');
    } finally {
      setBookingLoading(false);
    }
  };

  const handleAddProductToCart = (product, e) => {
    e.preventDefault();
    e.stopPropagation();
    dispatch(
      addToCart({
        productId: product._id,
        shopId: shop._id,
        name: product.name,
        price: product.price,
        image: product.images && product.images[0],
        quantity: 1,
        variants: product.variants || {},
      })
    );
    toast.success(`${product.name} added to cart`);
  };

  const errMsg = isError ? error?.message || 'Failed to load salon' : '';

  if (loading) {
    return (
      <Container>
        <div className={styles.stateCenter}>
          <Spinner />
          <Typography variant="body2" style={{ marginTop: 12 }}>
            Loading salon…
          </Typography>
        </div>
      </Container>
    );
  }

  if (errMsg || !shop) {
    return (
      <Container>
        <div className={styles.stateCenter}>
          <Typography variant="body1" className={styles.errorText}>
            {errMsg || 'Salon not found'}
          </Typography>
          <Button variant="primary" style={{ marginTop: 16 }} onClick={() => navigate('/salons')}>
            Back to salons
          </Button>
        </div>
      </Container>
    );
  }

  const heroSrc =
    shop.images?.[0] || 'https://images.unsplash.com/photo-1585747860715-2ba37e788b70?w=1200&q=80';
  const addressText = formatShopAddress(shop);
  const ownerName = shop.owner && typeof shop.owner === 'object' ? shop.owner.name : null;

  return (
    <Container>
      <div className={styles.page}>
        <button type="button" className={styles.backBtn} onClick={() => navigate(-1)}>
          <ChevronLeft size={18} aria-hidden />
          Back
        </button>

        <header className={styles.hero}>
          <img className={styles.heroImage} src={heroSrc} alt="" />
          <div className={styles.heroOverlay} aria-hidden />
          <div className={styles.heroContent}>
            <div className={styles.heroBadges}>
              {shop.verified ? (
                <Badge variant="success" size="sm">
                  Verified
                </Badge>
              ) : null}
              {shop.isOpen === false ? (
                <Badge variant="gray" size="sm">
                  Closed
                </Badge>
              ) : shop.isOpen === true ? (
                <Badge variant="secondary" size="sm">
                  Open now
                </Badge>
              ) : null}
            </div>
            <Typography variant="h1" fontSize="2.25rem" color="white" className={styles.heroTitle}>
              {shop.name}
            </Typography>
            <div className={styles.heroMeta}>
              <span className={styles.ratingLine}>
                <Star size={18} fill="currentColor" color="#fbbf24" aria-hidden />
                {shop.rating != null && !Number.isNaN(Number(shop.rating))
                  ? Number(shop.rating).toFixed(1)
                  : 'New'}
                {shop.totalReviews > 0 ? (
                  <span style={{ fontWeight: 500, opacity: 0.85 }}>
                    {' '}
                    · {shop.totalReviews} reviews
                  </span>
                ) : null}
              </span>
              {ownerName ? <span className={styles.ownerLine}>Owner · {ownerName}</span> : null}
            </div>
          </div>
        </header>

        <div className={styles.detailsCard}>
          {shop.description ? (
            <p className={styles.description}>{shop.description}</p>
          ) : (
            <p className={styles.description}>
              Professional cuts, styling, and grooming at this salon.
            </p>
          )}
          {addressText ? (
            <div className={styles.addressRow}>
              <MapPin size={18} aria-hidden />
              <span>{addressText}</span>
            </div>
          ) : null}
        </div>

        <div className={styles.layout}>
          <main>
            <h2 className={styles.sectionTitle}>Services &amp; catalog</h2>
            <p className={styles.sectionSub}>
              Choose a service for your appointment, or open a catalog item for full details and
              cart checkout.
            </p>

            {!hasBookable ? (
              <div className={styles.emptyOfferings}>
                <Typography variant="body1">This salon has not listed any services yet.</Typography>
                <Typography variant="body2" style={{ marginTop: 8, opacity: 0.85 }}>
                  Check back soon or explore other salons nearby.
                </Typography>
              </div>
            ) : (
              <>
                {services.length > 0 ? (
                  <>
                    <h3 className={styles.subsectionTitle}>
                      <Scissors
                        size={14}
                        style={{
                          display: 'inline',
                          verticalAlign: 'middle',
                          marginRight: 6,
                        }}
                      />
                      Bookable services
                    </h3>
                    <div className={styles.offerGrid}>
                      {services.map((s) => (
                        <button
                          key={s._id}
                          type="button"
                          className={`${styles.offerCard} ${
                            selectedService?._id === s._id ? styles.offerCardSelected : ''
                          }`}
                          onClick={() => {
                            setSelectedService(s);
                            setSelectedSlot(null);
                          }}
                        >
                          <div className={styles.offerCardTop}>
                            <p className={styles.offerName}>{s.name}</p>
                            <span className={styles.offerPrice}>₹{s.price}</span>
                          </div>
                          {s.category ? <p className={styles.offerMeta}>{s.category}</p> : null}
                          {formatDuration(s.duration) ? (
                            <p className={styles.offerMeta}>
                              <Clock
                                size={12}
                                style={{
                                  display: 'inline',
                                  verticalAlign: 'middle',
                                  marginRight: 4,
                                }}
                              />
                              {formatDuration(s.duration)}
                            </p>
                          ) : null}
                          {s.description ? (
                            <p className={styles.offerMeta}>{s.description}</p>
                          ) : null}
                          {selectedService?._id === s._id ? (
                            <span className={styles.offerHint}>Selected for booking →</span>
                          ) : (
                            <span className={styles.offerHint}>Tap to select</span>
                          )}
                        </button>
                      ))}
                    </div>
                  </>
                ) : null}

                {products.length > 0 ? (
                  <>
                    <h3 className={styles.subsectionTitle}>
                      <Sparkles
                        size={14}
                        style={{
                          display: 'inline',
                          verticalAlign: 'middle',
                          marginRight: 6,
                        }}
                      />
                      Shop catalog
                    </h3>
                    <div className={styles.productGrid}>
                      {products.map((p) => (
                        <div
                          key={p._id}
                          className={`${styles.productCard} ${
                            selectedService?._id === p._id ? styles.productCardSelected : ''
                          }`}
                        >
                          <div className={styles.productImageWrap}>
                            {p.images?.[0] ? (
                              <img src={p.images[0]} alt="" />
                            ) : (
                              <div className={styles.productPlaceholder}>✂️</div>
                            )}
                          </div>
                          <div className={styles.productBody}>
                            <p className={styles.productName}>{p.name}</p>
                            <p className={styles.productPrice}>₹{p.price}</p>
                            {p.category ? (
                              <p className={styles.offerMeta} style={{ marginBottom: 8 }}>
                                {p.category}
                              </p>
                            ) : null}
                            <div className={styles.productActions}>
                              <Button
                                variant="outline"
                                size="sm"
                                type="button"
                                onClick={() => navigate(`/services/${p._id}`)}
                              >
                                Details
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                type="button"
                                onClick={() => {
                                  setSelectedService(p);
                                  setSelectedSlot(null);
                                }}
                              >
                                Book slot
                              </Button>
                              <Button
                                variant="primary"
                                size="sm"
                                type="button"
                                onClick={(e) => handleAddProductToCart(p, e)}
                              >
                                Add to cart
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                ) : null}
              </>
            )}
          </main>

          <aside className={styles.bookingCard}>
            <h2 className={styles.bookingTitle}>Book a visit</h2>
            {!hasBookable ? (
              <Typography variant="body2" style={{ color: 'var(--text-secondary, #64748b)' }}>
                Nothing to book yet for this salon.
              </Typography>
            ) : (
              <>
                {selectedLabel ? (
                  <div className={styles.selectedPill}>
                    <strong>Service:</strong> {selectedLabel}
                  </div>
                ) : null}

                <label className={styles.fieldLabel} htmlFor="salon-book-date">
                  Date
                </label>
                <select
                  id="salon-book-date"
                  className={styles.select}
                  value={selectedDate}
                  onChange={(e) => {
                    setSelectedDate(e.target.value);
                    setSelectedSlot(null);
                  }}
                >
                  <option value="">Select date</option>
                  {nextDays.map((d) => (
                    <option key={d} value={d}>
                      {new Date(d + 'T12:00:00').toLocaleDateString('en-IN', {
                        weekday: 'short',
                        day: 'numeric',
                        month: 'short',
                      })}
                    </option>
                  ))}
                </select>

                <span className={styles.fieldLabel}>Time</span>
                <div className={styles.slotsWrap}>
                  {!selectedDate ? (
                    <Typography variant="body2" style={{ color: '#94a3b8' }}>
                      Pick a date to see slots
                    </Typography>
                  ) : slots.length === 0 ? (
                    <Typography variant="body2" style={{ color: '#94a3b8' }}>
                      No slots available
                    </Typography>
                  ) : (
                    slots.map((slot) => (
                      <button
                        key={slot.start}
                        type="button"
                        className={`${styles.slotBtn} ${
                          selectedSlot?.start === slot.start ? styles.slotBtnActive : ''
                        }`}
                        onClick={() => setSelectedSlot(slot)}
                      >
                        {slot.label}
                      </button>
                    ))
                  )}
                </div>

                {barbers.length > 0 ? (
                  <div className={styles.barbersSection}>
                    <p className={styles.barbersTitle}>Barber (optional)</p>
                    <div className={styles.barberChips}>
                      {barbers.map((b) => (
                        <button
                          key={b._id}
                          type="button"
                          className={`${styles.barberChip} ${
                            selectedBarber?._id === b._id ? styles.barberChipActive : ''
                          }`}
                          onClick={() =>
                            setSelectedBarber(selectedBarber?._id === b._id ? null : b)
                          }
                        >
                          <img
                            src={
                              b.profileImg ||
                              `https://ui-avatars.com/api/?name=${encodeURIComponent(b.name || '?')}`
                            }
                            alt=""
                          />
                          {b.name}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}

                <Button
                  variant="primary"
                  style={{ width: '100%' }}
                  onClick={handleBook}
                  disabled={!selectedSlot || bookingLoading}
                >
                  {bookingLoading ? 'Booking…' : 'Confirm booking'}
                </Button>
              </>
            )}
          </aside>
        </div>

        {reviews.length > 0 ? (
          <section className={styles.reviewsSection} aria-labelledby="reviews-h">
            <h2 id="reviews-h" className={styles.sectionTitle}>
              Reviews
            </h2>
            <p className={styles.sectionSub}>What clients say about this salon.</p>
            <div className={styles.reviewList}>
              {reviews.slice(0, 12).map((r) => (
                <article key={r._id} className={styles.reviewCard}>
                  <div className={styles.reviewHead}>
                    <Star size={14} fill="#fbbf24" color="#fbbf24" aria-hidden />
                    <span style={{ fontWeight: 600 }}>{r.rating}/5</span>
                    <span className={styles.reviewUser}>{r.user?.name || 'Client'}</span>
                  </div>
                  {r.comment ? <p className={styles.reviewComment}>{r.comment}</p> : null}
                </article>
              ))}
            </div>
          </section>
        ) : null}
      </div>
    </Container>
  );
}
